import classNames from "classnames";
import type {
  MRT_ColumnDef,
  MRT_TableInstance
} from "mantine-react-table-open";
import { MantineRoundedNumberCell } from "../../../../../mantine/mrt/components/cell/MantineRoundedNumberCell";
import { HeaderWithWarning } from "../../../../../mantine/mrt/components/header/HeaderWithWarning";
import { getColSum } from "../../../../../mantine/mrt/utils/getColSum";
import { EnergyRevenuesListItemMarket_premium_revenue_source } from "../../../../../types/api.types";
import type {
  EnergyRevenuesListItem,
  GetEnergyRevenuesTableColumnParams
} from "../../../RevenueMonitoring.types";
import { CustomTableCell } from "../CustomTableCell/CustomTableCell";
import { CustomTableHeader } from "../CustomTableCell/CustomTableHeader";
import { NullCell } from "../CustomTableCell/NullCell";
import { REVENUE_TABLE_HEADER_TEXTS } from "../EnergyRevenuesTable";
import { BASE_COLUMN_GROUPS } from "../EnergyRevenuesTable.constants";
import { isFirstVisibleColumn } from "../utils/isFirstVisibleColumn";

const PROJECTION_WARNING_TEXT = `Prognostizierter Abschlag des Netzbetreibers auf Basis des Monatsmarktwerts. Eine Korrektur erfolgt nach Bekanntgabe des Jahresmarktwerts.`;

export function getMarketPremiumRevenueColumn({
  companyId,
  dataVerificationColumns,
  dataVerificationItems,
  columnVisibility
}: GetEnergyRevenuesTableColumnParams): MRT_ColumnDef<EnergyRevenuesListItem> {
  const COLUMN_NAME = "market_premium_revenue";

  function hasProjectionWarning(
    table: MRT_TableInstance<EnergyRevenuesListItem>
  ) {
    return table.getRowModel().rows.some((row) => {
      const item = row.original;
      return (
        item.market_premium_revenue_source ===
        EnergyRevenuesListItemMarket_premium_revenue_source.projection_monatsmarktwert_for_jahresmarktwert
      );
    });
  }

  const isFirstColumn = isFirstVisibleColumn(
    COLUMN_NAME,
    columnVisibility,
    BASE_COLUMN_GROUPS.market_premium.children
  );

  return {
    accessorKey: COLUMN_NAME,
    header: REVENUE_TABLE_HEADER_TEXTS[COLUMN_NAME],
    size: 220,
    minSize: 150,
    mantineTableHeadCellProps: {
      className: classNames({ "column-header-border-left": isFirstColumn })
    },
    mantineTableBodyCellProps: {
      className: classNames({ "column-body-border-left": isFirstColumn })
    },
    mantineTableFooterCellProps: {
      className: classNames({ "column-body-border-left": isFirstColumn })
    },

    Header: ({ column, table }) => {
      if (hasProjectionWarning(table)) {
        return (
          <HeaderWithWarning column={column} label={PROJECTION_WARNING_TEXT} />
        );
      }
      return <CustomTableHeader column={column} table={table} />;
    },
    Cell: ({ row, column }) => {
      return (
        <CustomTableCell
          column={column}
          companyId={companyId}
          dataVerificationColumns={dataVerificationColumns}
          dataVerificationItems={dataVerificationItems}
          row={row}
        />
      );
    },
    Footer: ({ table }) => {
      const colSum = getColSum(table, COLUMN_NAME);
      if (colSum === null) {
        return <NullCell />;
      }
      return <MantineRoundedNumberCell extraSpaceRight value={colSum} />;
    },
    filterVariant: "range-slider"
  };
}
